import { Component } from 'preact';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Components
import Description from '../../Form/Description';
import Text from '../../Global/Text';

// Input Components
import InputCheckbox from './Checkbox';
import InputRadio from './Radio';
import InputRadioButtons from './Buttons/RadioButtons';
import InputCheckboxButtons from './Buttons/CheckboxButtons';
import ToggleSwitch from './ToggleSwitch';

import { stepShowHide } from '../../../utilities/form/formUtils';
import { getOptions } from '../../../utilities/common';
import { updateTranslationTextForConfirmationAction } from '../../../actions/UserData'; 

// Input Variants
const inputComponents = {
  'radio': InputRadio,
  'radioButtons': InputRadioButtons,
  'checkbox': InputCheckbox,
  'checkboxButtons': InputCheckboxButtons,
  'toggleSwitch': ToggleSwitch,
};

class InputGroup extends Component {
  constructor(props) {
    super(props);
    this.updateRadio = this.updateRadio.bind(this);
    const {
      values,
      id: groupName,
      options,
    } = this.props;
    this.state = {
      activeRadio: null,
      options: [],
      useLocal: Array.isArray(options),
    };
    // If Value already exists and it's true, then set to true again.
    if (Object.hasOwnProperty.call(values, groupName)) {
      this.state = {
        ...this.state,
        activeRadio: values[groupName],
      };
    }
  }

  componentDidMount() {
    const {
      initialValues,
      userData,
      dispatch,
      options,
      optionsFrom,
    } = this.props;

    if (optionsFrom) {
      getOptions(
        dispatch,
        options,
        optionsFrom,
        initialValues,
        userData
      ).then(data => {
        const { placeholderState } = this.state;
        let dataForRender = Array.isArray(data) && data.length > 0 ? data : [];
        if (Array.isArray(data) && data.length > 0 && typeof data[0] !== 'object') {
          dataForRender = data.reduce((result, item) => {
            return [
              ...result,
              {
                id: item,
                label: item,
              },
            ];
          }, []);
        }
        this.setState({
          options: dataForRender,
          placeholderState,
        });

        if (!placeholderState && !this.state.value
          && dataForRender && dataForRender.length > 0) {
          this.state.value = dataForRender[0].key;
          this.setState({
            value: dataForRender[0].key,
          });
        }
      });
    }

    //set Initial active on state 
    options.forEach(option => {
      if(option.activeRadio) {
        this.setState({
          activeRadio: option.activeRadio,
        });
      }
    })

    if(this.props.dynamicTitle) {
      let dynamicStr = this.props.dynamicTitleValue;
      if(userData[this.props.dynamicTitleValue]) {
        let val = userData[this.props.dynamicTitleValue];
        if(Array.isArray(val) && userData.staticQuotes) {
          val = val.map(item => {
            return userData.staticQuotes[item]?.label.toLowerCase();
          });
          dynamicStr = val.join('-, ');
          dynamicStr = dynamicStr.replace(/(,)(?!.*\1)/, ' eller');
        }
      }
      dispatch(updateTranslationTextForConfirmationAction({
        selectedInsuranceString: dynamicStr
      }));
    }
    
  }

  updateRadio(groupName, id) {
    this.setState({
      activeRadio: id,
    });
  }

  render() {
    const {
      id,
      type,
      columns,
      onChange,
      onBlur,
      desc,
      title,
      required,
      dispatch,
      isValid,
      errors,
      touched,
      setValues,
      values,
      className,
      variant,
      setFieldValue,
      options: propOptions,
      userData,
      radioType,
      radioTitle,
      tealiumMap,
    } = this.props;

    const { options, useLocal } = this.state;
    const renderOptions = useLocal ? propOptions : options;
    const isSingle = renderOptions.length === 1;
    const formGroupClass = classNames('form-group', {
      [`form-group--${type}`]: type,
      [`form-group--${columns}col`]: columns,
      [`form-group--${variant}`]: variant,
      [`${className}`]: className,
      'form-group--single': isSingle,
    });

    return (
      <div className={formGroupClass}>

        {/* Label */}
        {title &&
        <Text component="h4" className="form-group__headline" value={title}/>
        }

        {radioTitle &&
        <Text component="div" className="form__label" value={radioTitle}/>
        }

        {/* Description */}
        {desc &&
        <Description desc={desc}/>
        }

        <div className={radioType === 'equalHeight' ? 'form-group--button-section' : ''}>

          {/* Rendering options and input fields */}
          {renderOptions.map(option => {
            const formProps = {
              userData,
              onChange,
              onBlur,
              isValid,
              errors,
              touched,
              setValues,
              values,
              setFieldValue,
              radioType,
            };

            // Radio and Checkbox have different behavior, therefor we give them different props.
            const typeSpecific = () => {
              const { activeRadio } = this.state;
              switch (type) {
                case 'radioButtons':
                case 'radio': {
                  return {
                    activeRadio,
                    required,
                    updateRadio: this.updateRadio,
                  };
                }
                case 'checkboxButtons':
                case 'checkbox':
                case 'toggleSwitch': {
                  return { required: option.required || false };
                }
                default:
                  return {
                    required: false,
                  };
              }
            };
            const componentProps = {
              ...userData,
              ...option,
              ...typeSpecific(),
              ...(isSingle && !option.id) ? { id } : { 'groupName': id },
              ...formProps,
              hidden: (stepShowHide(option.hidden, option.showHide, userData, values)),
              key: option.key,
              type,
              dispatch,
              tealiumMap,
            };

            const InputComponent = inputComponents[type];
            return <InputComponent {...componentProps}/>;
          })
          }
        </div>
      </div>
    );
  }
}

export default InputGroup;

InputGroup.defaultProps = {
  desc: null,
  title: null,
  required: false,
  options: [],
  className: null,
  columns: '12',
  variant: null,
  userData: {},
  radioType: null,
  radioTitle: null,
  tealiumMap: null,
  optionsFrom: null,
};

InputGroup.propTypes = {
  id: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  columns: PropTypes.string,
  groupName: PropTypes.string,
  type: PropTypes.string.isRequired,
  isValid: PropTypes.bool,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  setValues: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  options: PropTypes.array,
  title: PropTypes.string,
  desc: PropTypes.string,
  required: PropTypes.string || PropTypes.bool,
  className: PropTypes.string,
  variant: PropTypes.string,
  setFieldValue: PropTypes.func,
  userData: PropTypes.object,
  radioType: PropTypes.string,
  radioTitle: PropTypes.string,
  tealiumMap: PropTypes.string,
  initialValues: PropTypes.object.isRequired,
  optionsFrom: PropTypes.string,
};
